import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppLayout } from '@/components/layout/AppLayout';
import { GuiaModuloFlutuante } from '@/components/shared/GuiaModuloFlutuante';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Archive,
  Boxes,
  ChevronRight,
  Clock,
  Copy,
  Edit,
  GitBranch,
  Link,
  Link2,
  Loader2,
  MoreHorizontal,
  Plus,
  Search,
  Share2,
  Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
import { fluxogramaNodesService, fluxogramasService } from '@/services/fluxogramaApi';
import type { Fluxograma, FluxogramaTemplate } from '@/types/fluxograma';
import { useAuth } from '@/contexts/AuthContext';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { AtrelarProjetoDialog } from '@/components/shared/AtrelarProjetoDialog';
import { projetotService } from '@/services/projetotApi';
import type { ProjetoT } from '@/types';

const tipoLabel: Record<Fluxograma['tipo'], string> = {
  processo: 'Processo',
  bpmn: 'BPMN',
  aprovacao: 'Aprovação',
  customizado: 'Customizado',
};

export default function FluxogramaPage() {
  const navigate = useNavigate();
  const { user, empresaAtiva, empresaFiltroId } = useAuth();
  const isAdmin = user?.tipo === 'admin' || user?.tipo === 'superadmin';
  const isSuperAdmin = user?.tipo === 'superadmin';

  const [fluxogramas, setFluxogramas] = useState<Fluxograma[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('todos');
  const [tipoFilter, setTipoFilter] = useState('todos');

  const effectiveEmpresaId = isSuperAdmin
    ? (empresaFiltroId || '')
    : empresaAtiva?.id || user?.empresa_id || '';

  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [templates, setTemplates] = useState<FluxogramaTemplate[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [creatingFromTemplate, setCreatingFromTemplate] = useState<string | null>(null);
  const [novoNome, setNovoNome] = useState('');
  const [novaDescricao, setNovaDescricao] = useState('');
  const [templateSelecionado, setTemplateSelecionado] = useState<FluxogramaTemplate | null>(null);

  const [confirmDelete, setConfirmDelete] = useState<Fluxograma | null>(null);

  const [editingFluxograma, setEditingFluxograma] = useState<Fluxograma | null>(null);
  const [editForm, setEditForm] = useState({ nome: '', descricao: '', tipo: 'processo' as Fluxograma['tipo'], status: 'rascunho' as Fluxograma['status'] });
  const [isSavingEdit, setIsSavingEdit] = useState(false);

  const [projetosT, setProjetosT] = useState<ProjetoT[]>([]);
  const [atrelarTarget, setAtrelarTarget] = useState<Fluxograma | null>(null);

  const carregar = useCallback(async () => {
    if (!effectiveEmpresaId) {
      setFluxogramas([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const result = await fluxogramasService.getAll(isSuperAdmin ? effectiveEmpresaId : undefined);
      if (result.success) setFluxogramas(result.data || []);
      else toast.error(result.error || 'Erro ao carregar fluxogramas');
    } catch (err) {
      toast.error('Erro ao carregar fluxogramas');
    } finally {
      setLoading(false);
    }
  }, [effectiveEmpresaId, isSuperAdmin]);

  // Recarrega ao trocar de empresa — evita exibir dados da empresa anterior.
  useEffect(() => {
    setFluxogramas([]);
    carregar();
  }, [carregar, effectiveEmpresaId]);

  useEffect(() => {
    if (!effectiveEmpresaId) {
      setProjetosT([]);
      return;
    }
    projetotService.getAll(undefined, isSuperAdmin ? effectiveEmpresaId : undefined).then((result) => {
      if (result.success) setProjetosT(result.data || []);
    });
  }, [effectiveEmpresaId, isSuperAdmin]);

  const handleAtrelarProjeto = async (projetotId: string | null) => {
    if (!atrelarTarget) return;
    const result = await fluxogramasService.update(atrelarTarget.id, { projetot_id: projetotId });
    if (result.success) {
      toast.success('Vínculo atualizado');
      carregar();
    } else {
      toast.error(result.error || 'Erro ao atrelar projeto');
    }
  };

  const carregarTemplates = useCallback(async () => {
    setLoadingTemplates(true);
    try {
      const result = await fluxogramaNodesService.listTemplates();
      if (result.success) setTemplates(result.data || []);
    } catch (_) {
      // ignora — usa array vazio
    } finally {
      setLoadingTemplates(false);
    }
  }, []);

  const handleNovo = () => {
    setNovoNome('');
    setNovaDescricao('');
    setTemplateSelecionado(null);
    setTemplatesOpen(true);
    if (templates.length === 0) carregarTemplates();
  };

  const handleConfirmarCriacao = async () => {
    if (!templateSelecionado) {
      toast.error('Selecione um template');
      return;
    }
    if (!novoNome.trim()) {
      toast.error('Informe o nome do fluxograma');
      return;
    }
    if (isSuperAdmin && !effectiveEmpresaId) {
      toast.error('Selecione uma empresa');
      return;
    }
    setCreatingFromTemplate(templateSelecionado.key);
    try {
      const createResult = await fluxogramasService.create({
        nome: novoNome.trim(),
        descricao: novaDescricao.trim() || null,
        tipo: templateSelecionado.tipo,
        status: 'rascunho',
        visibilidade: 'empresa',
        template_key: templateSelecionado.key,
        ...(isSuperAdmin ? { empresa_id: effectiveEmpresaId } : {}),
      });
      if (!createResult.success) {
        toast.error(createResult.error || 'Erro ao criar fluxograma');
        return;
      }

      const novo = createResult.data;

      toast.success('Fluxograma criado');
      setTemplatesOpen(false);
      navigate(`/fluxogramas/${novo.id}`);
    } catch (_) {
      toast.error('Erro ao criar fluxograma');
    } finally {
      setCreatingFromTemplate(null);
    }
  };

  const openEditFluxograma = (item: Fluxograma) => {
    setEditingFluxograma(item);
    setEditForm({
      nome: item.nome,
      descricao: item.descricao || '',
      tipo: item.tipo,
      status: item.status,
    });
  };

  const handleSaveEdit = async () => {
    if (!editingFluxograma) return;
    if (!editForm.nome.trim()) {
      toast.error('Informe o nome do fluxograma');
      return;
    }
    setIsSavingEdit(true);
    try {
      const result = await fluxogramasService.update(editingFluxograma.id, {
        nome: editForm.nome,
        descricao: editForm.descricao || null,
        tipo: editForm.tipo,
        status: editForm.status,
      });
      if (result.success) {
        toast.success('Fluxograma atualizado');
        setEditingFluxograma(null);
        carregar();
      } else {
        toast.error(result.error || 'Erro ao atualizar fluxograma');
      }
    } finally {
      setIsSavingEdit(false);
    }
  };

  const handleDuplicar = async (item: Fluxograma) => {
    try {
      const result = await fluxogramasService.duplicate(item.id);
      if (result.success) {
        toast.success('Fluxograma duplicado');
        carregar();
      } else {
        toast.error(result.error || 'Erro ao duplicar fluxograma');
      }
    } catch (_) {
      toast.error('Erro ao duplicar fluxograma');
    }
  };

  const handleArquivar = async (item: Fluxograma) => {
    try {
      const result = await fluxogramasService.archive(item.id);
      if (result.success) {
        toast.success('Fluxograma arquivado');
        carregar();
      } else {
        toast.error(result.error || 'Erro ao arquivar fluxograma');
      }
    } catch (_) {
      toast.error('Erro ao arquivar fluxograma');
    }
  };

  const handleShare = async (item: Fluxograma) => {
    try {
      if (item.visibilidade === 'publico_link' && item.share_token) {
        const url = `${window.location.origin}/f/${item.share_token}`;
        await navigator.clipboard.writeText(url).catch(() => undefined);
        toast.success('Link público copiado para a área de transferência');
        return;
      }
      const result = await fluxogramasService.generateShareLink(item.id);
      if (result.success && result.data.share_token) {
        const url = `${window.location.origin}/f/${result.data.share_token}`;
        await navigator.clipboard.writeText(url).catch(() => undefined);
        toast.success('Link público gerado e copiado');
        carregar();
      } else {
        toast.error(result.error || 'Erro ao gerar link público');
      }
    } catch (_) {
      toast.error('Erro ao gerar link público');
    }
  };

  const handleRevoke = async (item: Fluxograma) => {
    try {
      const result = await fluxogramasService.revokeShareLink(item.id);
      if (result.success) {
        toast.success('Link público revogado');
        carregar();
      } else {
        toast.error(result.error || 'Erro ao revogar link');
      }
    } catch (_) {
      toast.error('Erro ao revogar link');
    }
  };

  const handleConfirmDelete = async () => {
    if (!confirmDelete) return;
    try {
      const result = await fluxogramasService.delete(confirmDelete.id);
      if (result.success) {
        toast.success('Fluxograma excluído');
        carregar();
      } else {
        toast.error(result.error || 'Erro ao excluir fluxograma');
      }
    } catch (_) {
      toast.error('Erro ao excluir fluxograma');
    } finally {
      setConfirmDelete(null);
    }
  };

  const filtrados = useMemo(() => {
    const s = search.trim().toLowerCase();
    return fluxogramas.filter((f) => {
      const matchesSearch =
        !s ||
        f.nome.toLowerCase().includes(s) ||
        (f.descricao || '').toLowerCase().includes(s) ||
        tipoLabel[f.tipo].toLowerCase().includes(s);
      const matchesStatus = statusFilter === 'todos' || f.status === statusFilter;
      const matchesTipo = tipoFilter === 'todos' || f.tipo === tipoFilter;
      return matchesSearch && matchesStatus && matchesTipo;
    });
  }, [search, fluxogramas, statusFilter, tipoFilter]);

  return (
    <AppLayout>
      <div className="space-y-6">
        <header className="rounded-lg border bg-card p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold flex items-center gap-2">
              <GitBranch className="h-6 w-6" />
              Fluxogramas
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Mapeie processos com formas ANSI e BPMN, atribua responsáveis e compartilhe via link.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {isAdmin && (
              <Button onClick={handleNovo} disabled={isSuperAdmin && !effectiveEmpresaId} data-tour="novo-fluxograma">
                <Plus className="h-4 w-4 mr-2" /> Novo fluxograma
              </Button>
            )}
          </div>
        </header>

        <div className="grid gap-3 md:grid-cols-[1fr_180px_180px] rounded-md border bg-muted/50 p-3">
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Buscar por nome, descrição ou tipo…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 bg-background"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="bg-background">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todos os status</SelectItem>
              <SelectItem value="rascunho">Rascunho</SelectItem>
              <SelectItem value="ativo">Ativo</SelectItem>
              <SelectItem value="arquivado">Arquivado</SelectItem>
            </SelectContent>
          </Select>
          <Select value={tipoFilter} onValueChange={setTipoFilter}>
            <SelectTrigger className="bg-background">
              <SelectValue placeholder="Tipo" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todos os tipos</SelectItem>
              <SelectItem value="processo">Processo</SelectItem>
              <SelectItem value="bpmn">BPMN</SelectItem>
              <SelectItem value="aprovacao">Aprovação</SelectItem>
              <SelectItem value="customizado">Customizado</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {isSuperAdmin && !effectiveEmpresaId ? (
          <Card>
            <CardHeader>
              <CardTitle>Selecione uma empresa</CardTitle>
              <CardDescription>
                Fluxogramas são por empresa — escolha uma empresa específica no filtro do topo (está em "Todas as empresas").
              </CardDescription>
            </CardHeader>
          </Card>
        ) : loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-40 w-full rounded-lg" />
            ))}
          </div>
        ) : filtrados.length === 0 ? (
          <Card>
            <CardHeader>
              <CardTitle>Nenhum fluxograma encontrado</CardTitle>
              <CardDescription>
                {fluxogramas.length === 0
                  ? 'Crie seu primeiro fluxograma a partir de um template.'
                  : 'Ajuste a busca ou os filtros para encontrar o fluxograma desejado.'}
              </CardDescription>
            </CardHeader>
            {isAdmin && fluxogramas.length === 0 && (
              <CardContent>
                <Button onClick={handleNovo}>
                  <Plus className="h-4 w-4 mr-2" /> Novo fluxograma
                </Button>
              </CardContent>
            )}
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtrados.map((f) => (
              <Card
                key={f.id}
                className="group relative cursor-pointer overflow-hidden border-border/80 bg-card shadow-sm transition-all hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-lg"
                onClick={() => navigate(`/fluxogramas/${f.id}`)}
              >
                {f.share_token && (
                  <Badge variant="secondary" className="absolute right-2 top-2 z-10 gap-1 text-[10px] text-emerald-600">
                    <Link2 className="h-3 w-3" />
                    Público
                  </Badge>
                )}
                <CardContent className="space-y-4 p-5">
                  <div className="flex items-start gap-3">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary ring-1 ring-primary/20 transition-transform group-hover:scale-105">
                      <GitBranch className="h-5 w-5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h2 className="line-clamp-2 min-h-[2.8rem] break-words text-lg font-semibold leading-tight">{f.nome}</h2>
                      <div className="mt-3 flex flex-wrap gap-2">
                        <Badge variant="outline">{tipoLabel[f.tipo]}</Badge>
                        <StatusBadge status={f.status} />
                        {f.projetoT && (
                          <Badge
                            variant="outline"
                            className="gap-1 cursor-pointer hover:bg-accent"
                            onClick={(e) => { e.stopPropagation(); navigate(`/projetos/${f.projetoT!.id}`); }}
                          >
                            <Link className="h-3 w-3" />
                            {f.projetoT.titulo}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>

                  <p className="line-clamp-2 text-sm leading-5 text-muted-foreground">
                    {f.descricao || 'Sem descrição cadastrada.'}
                  </p>

                  <div className="flex flex-col gap-2 rounded-xl border bg-muted/20 px-3 py-2.5 text-sm">
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Boxes className="h-3.5 w-3.5 shrink-0 text-primary" />
                      <span><strong className="font-semibold text-foreground">{f.total_nodes ?? 0}</strong> nós</span>
                    </div>
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Clock className="h-3.5 w-3.5 shrink-0 text-primary" />
                      <span>Atualizado em <strong className="font-semibold text-foreground">{formatDate(f.updatedAt)}</strong></span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 border-t pt-4">
                    <Button className="flex-1" size="sm" onClick={() => navigate(`/fluxogramas/${f.id}`)}>
                      Abrir fluxograma
                      <ChevronRight className="ml-2 h-4 w-4" />
                    </Button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button size="sm" variant="outline" aria-label="Ações" onClick={(e) => e.stopPropagation()}>
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                        {isAdmin && (
                          <DropdownMenuItem onClick={() => openEditFluxograma(f)}>
                            <Edit className="h-4 w-4 mr-2" /> Editar
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem onClick={() => handleShare(f)}>
                          <Share2 className="h-4 w-4 mr-2" /> Compartilhar
                        </DropdownMenuItem>
                        {f.share_token && (
                          <DropdownMenuItem onClick={() => handleRevoke(f)}>
                            <Link2 className="h-4 w-4 mr-2" /> Revogar link
                          </DropdownMenuItem>
                        )}
                        {isAdmin && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => setAtrelarTarget(f)}>
                              <Link className="h-4 w-4 mr-2" /> Atrelar a um projeto
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleDuplicar(f)}>
                              <Copy className="h-4 w-4 mr-2" /> Duplicar
                            </DropdownMenuItem>
                            {f.status !== 'arquivado' && (
                              <DropdownMenuItem onClick={() => handleArquivar(f)}>
                                <Archive className="h-4 w-4 mr-2" /> Arquivar
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              className="text-red-600 focus:text-red-600"
                              onClick={() => setConfirmDelete(f)}
                            >
                              <Trash2 className="h-4 w-4 mr-2" /> Excluir
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      <Dialog open={templatesOpen} onOpenChange={setTemplatesOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Novo fluxograma</DialogTitle>
            <DialogDescription>Comece a partir de um template ou em branco.</DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 max-h-[50vh] overflow-y-auto pr-1">
            {loadingTemplates && templates.length === 0 ? (
              Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-28 w-full" />)
            ) : (
              templates.map((t) => {
                const selected = templateSelecionado?.key === t.key;
                return (
                  <button
                    type="button"
                    key={t.key}
                    onClick={() => setTemplateSelecionado(t)}
                    className={`text-left rounded-lg border p-3 transition-colors ${
                      selected ? 'border-primary bg-primary/5' : 'hover:border-primary/40'
                    }`}
                  >
                    <div className="font-medium">{t.nome}</div>
                    <div className="text-xs text-muted-foreground mt-1">{t.descricao}</div>
                    <div className="text-xs text-muted-foreground mt-2">
                      {t.nodes.length} nós · {t.edges.length} conexões
                    </div>
                  </button>
                );
              })
            )}
          </div>

          <div className="grid gap-3 pt-2">
            <div>
              <Label htmlFor="fluxo-nome">Nome *</Label>
              <Input
                id="fluxo-nome"
                value={novoNome}
                onChange={(e) => setNovoNome(e.target.value)}
                placeholder="Ex.: Processo de aprovação"
              />
            </div>
            <div>
              <Label htmlFor="fluxo-desc">Descrição</Label>
              <Textarea
                id="fluxo-desc"
                value={novaDescricao}
                onChange={(e) => setNovaDescricao(e.target.value)}
                rows={2}
                placeholder="Sobre o que é este fluxograma"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setTemplatesOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleConfirmarCriacao} disabled={!!creatingFromTemplate}>
              {creatingFromTemplate ? 'Criando…' : 'Criar fluxograma'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editingFluxograma} onOpenChange={(open) => !open && setEditingFluxograma(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Editar fluxograma</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4">
            <div className="grid gap-2">
              <Label htmlFor="fluxo-edit-nome">Nome</Label>
              <Input
                id="fluxo-edit-nome"
                value={editForm.nome}
                onChange={(e) => setEditForm({ ...editForm, nome: e.target.value })}
                placeholder="Ex: Processo de aprovação"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="fluxo-edit-descricao">Descrição</Label>
              <Textarea
                id="fluxo-edit-descricao"
                value={editForm.descricao}
                onChange={(e) => setEditForm({ ...editForm, descricao: e.target.value })}
                placeholder="Sobre o que é este fluxograma"
              />
            </div>
            <div className="grid gap-2">
              <Label>Tipo</Label>
              <Select
                value={editForm.tipo}
                onValueChange={(value: Fluxograma['tipo']) => setEditForm({ ...editForm, tipo: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="processo">Processo</SelectItem>
                  <SelectItem value="bpmn">BPMN</SelectItem>
                  <SelectItem value="aprovacao">Aprovação</SelectItem>
                  <SelectItem value="customizado">Customizado</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label>Status</Label>
              <Select
                value={editForm.status}
                onValueChange={(value: Fluxograma['status']) => setEditForm({ ...editForm, status: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="rascunho">Rascunho</SelectItem>
                  <SelectItem value="ativo">Ativo</SelectItem>
                  <SelectItem value="arquivado">Arquivado</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingFluxograma(null)}>
              Cancelar
            </Button>
            <Button onClick={handleSaveEdit} disabled={isSavingEdit}>
              {isSavingEdit && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir fluxograma?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação não pode ser desfeita. O fluxograma "{confirmDelete?.nome}" e todos os seus nós e conexões
              serão removidos permanentemente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
              onClick={handleConfirmDelete}
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AtrelarProjetoDialog
        open={!!atrelarTarget}
        onOpenChange={(open) => { if (!open) setAtrelarTarget(null); }}
        projetos={projetosT}
        currentId={atrelarTarget?.projetot_id}
        onConfirm={handleAtrelarProjeto}
      />

      <GuiaModuloFlutuante imagemSrc="/guias-modulos/fluxogramas.png" titulo="Fluxogramas" />
    </AppLayout>
  );
}

function formatDate(value?: string) {
  if (!value) return 'Sem registro';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Sem registro';
  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}
